import { useState, useContext, useEffect, useRef, useCallback } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { useLocation } from 'react-router-dom'
import useArrayDiff from '../hooks/useArrayDiff'
import usePrevious from '../hooks/usePrevious'
import { AudioSourceInfo, UserContext, UserContextType } from '../UserContext'
import { logger } from './Logger'
import useUserContextRef from './useUserContextRef'
import useMediaSelectionActions from '../actions/useMediaSelectionActions'
import useAppContextRef from './useAppContextRef'
import useMediaSourceActions from '../functionalities/MediaSources/useMediaSourceActions'
import { Trigger } from '@/utlis/Trigger'

const showPromptLocationLookup = {
    '/pages/host-session-settings': true, // pre-redesign route
    '/pages/collaborate': true, // pre-redesign route
    '/collaborate': true,
} as { [key: string]: boolean }

const log = logger.createChildLogger({ name: 'useTriggerPromptsOnMediaSourcesUpdate' })

export const testDeviceAddedTrigger = new Trigger<void>()
export const testDeviceRemovedTrigger = new Trigger<void>()

/**
 * Listens for changes to the `state.mediaSources` and notifies the user of
 * the changes and, under certain circumstances, allows them to select a new device.
 */
export default function useTriggerPromptsOnMediaSourcesUpdate() {
    const location = useLocation()

    const { state, dispatch } = useContext(UserContext) as UserContextType

    const { appStateRef } = useAppContextRef()
    const { userStateRef } = useUserContextRef()

    const { addAudioDeviceSelection } = useMediaSelectionActions()

    const { getCurrentSystemDefaultOutputDevice } = useMediaSourceActions()

    const prevMediaSources = usePrevious(state.mediaSources)

    const audioInputsDiff = useArrayDiff(state.mediaSources.audioInputs, (a, b) => a.deviceId === b.deviceId)
    const audioOutputsDiff = useArrayDiff(state.mediaSources.audioOutputs, (a, b) => a.deviceId === b.deviceId)
    const videoSourcesDiff = useArrayDiff(state.mediaSources.video, (a, b) => a.deviceId === b.deviceId)

    // the actual prompts to be shown at the current moment.
    const [newDevicePrompts, set_newDevicePrompts] = useState<NewDevicePromptData[]>([])
    const [deviceRemovedPrompts, set_deviceRemovedPrompts] = useState<DeviceRemovedPromptData[]>([])

    /**
     * Keep a list of recently removed `mediaSelection.audioInputs`; we will refer to this list
     * to see if a removed device was being used, and show device removed dialog if so.
     */
    const mediaSelectionAudioInputsDiff = useArrayDiff(
        state.mediaSelection.audioInputs,
        (a, b) => a.deviceId === b.deviceId
    )
    const recentlyRemovedMediaSelectionAudioInputsRef = useRef<Set<string>>(new Set())
    const recentlyRemovedMediaSelectionAudioInputsTimeoutRef = useRef<NodeJS.Timeout | undefined>()
    useEffect(() => {
        if (!mediaSelectionAudioInputsDiff) {
            return
        }

        mediaSelectionAudioInputsDiff.removed.forEach((removed) => {
            log.verbose(
                'recentlyRemovedMediaSelectionAudioInputsRef',
                `adding "${removed.deviceId}" to recentlyRemovedMediaSelectionAudioInputsRef.`
            )
            recentlyRemovedMediaSelectionAudioInputsRef.current.add(removed.deviceId)
            clearTimeout(recentlyRemovedMediaSelectionAudioInputsTimeoutRef.current)
            recentlyRemovedMediaSelectionAudioInputsTimeoutRef.current = setTimeout(() => {
                recentlyRemovedMediaSelectionAudioInputsRef.current.delete(removed.deviceId)
                log.verbose(
                    'recentlyRemovedMediaSelectionAudioInputsRef',
                    `removed "${removed.deviceId}" to recentlyRemovedMediaSelectionAudioInputsRef.`
                )
            }, 5000)
        })
    }, [mediaSelectionAudioInputsDiff, state.mediaSelection.audioInputs])

    useEffect(
        function mediaSourceChangedEffect() {
            if (prevMediaSources == null) {
                log.verbose('mediaSourceChangedEffect', `prevMediaSources==null so no-op.`)
                return
            }

            if (appStateRef.current.appSettings.noMediaSourcesChangedPrompt) {
                log.verbose('mediaSourceChangedEffect', `noMediaSourcesChangedPrompt is true so no-op.`)
                return
            }

            if (state.mediaSources.updatedByEventCt <= prevMediaSources.updatedByEventCt) {
                log.verbose(
                    'mediaSourceChangedEffect',
                    `not a mediaSources update that was done ` + `by an event handler. No-op.`
                )
                return
            }

            if (!showPromptLocationLookup[location.pathname]) {
                log.verbose(
                    'mediaSourceChangedEffect',
                    `current location is "${location.pathname}" ` + `which is not in 'showPromptLocationLookup'; no-op.`
                )
                return
            }

            log.info('mediaSourceChangedEffect', ``, {
                audioInputsDiff: {
                    added: audioInputsDiff.added.map((a) => a.label),
                    removed: audioInputsDiff.removed.map((a) => a.label),
                },
                audioOutputsDiff: {
                    added: audioOutputsDiff.added.map((a) => a.label),
                    removed: audioOutputsDiff.removed.map((a) => a.label),
                },
                videoSourcesDiff: {
                    added: videoSourcesDiff.added.map((a) => a.label),
                    removed: videoSourcesDiff.removed.map((a) => a.label),
                },
                newDevicePrompts: newDevicePrompts,
                deviceRemovedPrompts: deviceRemovedPrompts,
                'location/pathname': location.pathname,
            })

            //
            // Set newDevicePromptData or deviceRemovedPromptData according each type of
            // device and whether they were added or removed.
            // Note that on a single effect, both audioInput and audioOutput device can be
            // added/removed.
            //

            let newDevicePromptsNext = structuredClone(newDevicePrompts)
            let deviceRemovedPromptsNext = structuredClone(deviceRemovedPrompts)

            // handle added
            for (let added of audioInputsDiff.added) {
                // ignore if in `autoRemovedAudioInputs`
                if (userStateRef.current.mediaSelection.autoRemovedAudioInputs[added.deviceId]) {
                    continue
                }

                // ignore if suppressed
                if (appStateRef.current.appSettings.newDevicePromptSuppressedLabels[added.displayLabel]) {
                    continue
                }

                newDevicePromptsNext = newDevicePromptsNext.filter(
                    (d) => !(d.deviceId === added.deviceId && d.deviceType === 'audioInput')
                )
                deviceRemovedPromptsNext = deviceRemovedPromptsNext.filter(
                    (d) => !(d.deviceId === added.deviceId && d.deviceType === 'audioInput')
                )
                newDevicePromptsNext.push({
                    deviceLabel: added.displayLabel,
                    deviceId: added.deviceId,
                    deviceType: 'audioInput',
                })
            }

            for (let added of audioOutputsDiff.added) {
                // ignore if suppressed
                if (appStateRef.current.appSettings.newDevicePromptSuppressedLabels[added.displayLabel]) {
                    continue
                }

                newDevicePromptsNext = newDevicePromptsNext.filter(
                    (d) => !(d.deviceId === added.deviceId && d.deviceType === 'audioOutput')
                )
                deviceRemovedPromptsNext = deviceRemovedPromptsNext.filter(
                    (d) => !(d.deviceId === added.deviceId && d.deviceType === 'audioOutput')
                )
                newDevicePromptsNext.push({
                    deviceLabel: added.label,
                    deviceId: added.deviceId,
                    deviceType: 'audioOutput',
                })
            }

            for (let added of videoSourcesDiff.added) {
                // ignore if suppressed
                if (appStateRef.current.appSettings.newDevicePromptSuppressedLabels[added.label]) {
                    continue
                }

                newDevicePromptsNext = newDevicePromptsNext.filter(
                    (d) => !(d.deviceId === added.deviceId && d.deviceType === 'video')
                )
                deviceRemovedPromptsNext = deviceRemovedPromptsNext.filter(
                    (d) => !(d.deviceId === added.deviceId && d.deviceType === 'video')
                )
                newDevicePromptsNext.push({
                    deviceLabel: added.label,
                    deviceId: added.deviceId,
                    deviceType: 'video',
                })
            }

            // handle removed
            // NOTE: do not show for devices that were not being used.
            for (let removed of audioInputsDiff.removed) {
                deviceRemovedPromptsNext = deviceRemovedPromptsNext.filter(
                    (d) => !(d.deviceId === removed.deviceId && d.deviceType === 'audioInput')
                )
                newDevicePromptsNext = newDevicePromptsNext.filter(
                    (d) => !(d.deviceId === removed.deviceId && d.deviceType === 'audioInput')
                )
                let wasBeingUsed =
                    userStateRef.current.mediaSelection.audioInputs.find((b) => b.deviceId === removed.deviceId) !=
                        null || recentlyRemovedMediaSelectionAudioInputsRef.current.has(removed.deviceId)
                if (wasBeingUsed) {
                    deviceRemovedPromptsNext.push({
                        deviceLabel: removed.displayLabel,
                        deviceId: removed.deviceId,
                        deviceType: 'audioInput',
                    })
                }
            }

            for (let removed of audioOutputsDiff.removed) {
                deviceRemovedPromptsNext = deviceRemovedPromptsNext.filter(
                    (d) => !(d.deviceId === removed.deviceId && d.deviceType === 'audioOutput')
                )
                newDevicePromptsNext = newDevicePromptsNext.filter(
                    (d) => !(d.deviceId === removed.deviceId && d.deviceType === 'audioOutput')
                )
                let wasBeingUsed = userStateRef.current.mediaSelection.audioOutput === removed.deviceId
                if (wasBeingUsed) {
                    deviceRemovedPromptsNext.push({
                        deviceLabel: removed.label,
                        deviceId: removed.deviceId,
                        deviceType: 'audioOutput',
                    })
                }
            }

            for (let removed of videoSourcesDiff.removed) {
                deviceRemovedPromptsNext = deviceRemovedPromptsNext.filter(
                    (d) => !(d.deviceId === removed.deviceId && d.deviceType === 'video')
                )
                newDevicePromptsNext = newDevicePromptsNext.filter(
                    (d) => !(d.deviceId === removed.deviceId && d.deviceType === 'video')
                )
                if (userStateRef.current.mediaSelection.previousVideo === removed.deviceId) {
                    deviceRemovedPromptsNext.push({
                        deviceLabel: removed.label,
                        deviceId: removed.deviceId,
                        deviceType: 'video',
                    })
                }
            }

            log.info('mediaSourceChangedEffect', `done (before culling).`, {
                newDevicePromptsNext,
                deviceRemovedPromptsNext,
            })

            // only show the last of each type for `newDevicePromptsNext`
            {
                const ai = newDevicePromptsNext.reverse().find((a) => a.deviceType === 'audioInput') || null
                const ao = newDevicePromptsNext.reverse().find((a) => a.deviceType === 'audioOutput') || null
                const vi = newDevicePromptsNext.reverse().find((a) => a.deviceType === 'video') || null
                newDevicePromptsNext = [ai, ao, vi].filter((a) => a != null) as NewDevicePromptData[]
            }

            // only show the last of each type for `deviceRemovedPromptsNext`
            {
                const ai = deviceRemovedPromptsNext.reverse().find((a) => a.deviceType === 'audioInput') || null
                const ao = deviceRemovedPromptsNext.reverse().find((a) => a.deviceType === 'audioOutput') || null
                const vi = deviceRemovedPromptsNext.reverse().find((a) => a.deviceType === 'video') || null
                deviceRemovedPromptsNext = [ai, ao, vi].filter((a) => a != null) as DeviceRemovedPromptData[]
            }

            log.info('mediaSourceChangedEffect', `done.`, {
                newDevicePromptsNext,
                deviceRemovedPromptsNext,
            })
            unstable_batchedUpdates(() => {
                set_newDevicePrompts(newDevicePromptsNext)
                set_deviceRemovedPrompts(deviceRemovedPromptsNext)
            })
        },
        [
            prevMediaSources,
            state.mediaSources,
            audioInputsDiff,
            audioOutputsDiff,
            videoSourcesDiff,
            newDevicePrompts,
            deviceRemovedPrompts,
            location.pathname,
            appStateRef,
            userStateRef,
        ]
    )

    // recover selected audioInput devices using the `mediaSelection.autoRemovedAudioInputs`
    // when the mediaSources list (`state.mediaSources`) changes.
    //
    const recoverAutoRemovedAudioDevicesTimer = useRef<NodeJS.Timeout | undefined>()
    useEffect(
        function recoverAutoRemovedAudioDevices() {
            log.verbose('recoverAutoRemovedAudioDevices', 'triggered.')

            async function recoverAutoRemovedAudioDevicesDebounced() {
                const autoRemovedAudioInputs = userStateRef.current.mediaSelection.autoRemovedAudioInputs
                const audioInputs = userStateRef.current.mediaSelection.audioInputs

                log.verbose(
                    'recoverAutoRemovedAudioDevices',
                    'running debounced function (recoverAutoRemovedAudioDevicesDebounced).',
                    {
                        mediaSourcesAudioInputs: userStateRef.current.mediaSources.audioInputs.map(
                            (a) => `(deviceId="${a.deviceId}", label="${a.label}")`
                        ),
                        autoRemovedAudioInputs: Object.keys(autoRemovedAudioInputs),
                        mediaSelectionAudioInputs: audioInputs.map((a) => `(deviceId="${a.deviceId}"`),
                    }
                )

                let devicesToAdd: AudioSourceInfo[] = []
                userStateRef.current.mediaSources.audioInputs.forEach((a) => {
                    if (autoRemovedAudioInputs[a.deviceId]) {
                        if (audioInputs.find((b) => b.deviceId === a.deviceId)) {
                            log.verbose(
                                'recoverAutoRemovedAudioDevices',
                                `deviceId="${a.deviceId}" is in autoRemovedAudioInputs, ` +
                                    `but skipping because it is already selected in audioInputs.`
                            )
                        } else {
                            devicesToAdd.push(a)
                        }
                    }
                })

                if (devicesToAdd.length > 0) {
                    log.info(
                        'recoverAutoRemovedAudioDevices',
                        'devicesToAdd=' + JSON.stringify(devicesToAdd.map((a) => a.deviceId))
                    )
                    addAudioDeviceSelection(devicesToAdd)
                } else {
                    log.verbose(
                        'recoverAutoRemovedAudioDevices',
                        'devicesToAdd=' + JSON.stringify(devicesToAdd.map((a) => a.deviceId))
                    )
                }
            }

            clearTimeout(recoverAutoRemovedAudioDevicesTimer.current)
            recoverAutoRemovedAudioDevicesTimer.current = setTimeout(() => {
                recoverAutoRemovedAudioDevicesDebounced()
            }, 2000)

            return () => {
                clearTimeout(recoverAutoRemovedAudioDevicesTimer.current)
            }
        },
        [state.mediaSources, state.mediaSelection.autoRemovedAudioInputs]
    )

    useEffect(() => {
        const added = () => {
            set_newDevicePrompts([
                {
                    deviceLabel: 'Blue Microphones Analog Stereo',
                    deviceId: 'testdevice',
                    deviceType: 'video',
                },
            ])
        }
        const removed = () => {
            set_deviceRemovedPrompts([
                {
                    deviceLabel: 'Blue Microphones Analog Stereo',
                    deviceId: 'testdevice',
                    deviceType: 'audioInput',
                },
            ])
        }
        testDeviceAddedTrigger.on('trigger', added)
        testDeviceRemovedTrigger.on('trigger', removed)
        return () => {
            testDeviceAddedTrigger.off('trigger', added)
            testDeviceRemovedTrigger.off('trigger', removed)
        }
    }, [])

    const removeNewDevicePromptItem = useCallback((device: NewDevicePromptData) => {
        set_newDevicePrompts((list) => {
            return list.filter((d) => !(d.deviceId === device.deviceId && d.deviceType === device.deviceType))
        })
    }, [])

    const removeDeviceRemovedPromptItem = useCallback((device: DeviceRemovedPromptData) => {
        set_deviceRemovedPrompts((list) => {
            return list.filter((d) => !(d.deviceId === device.deviceId && d.deviceType === device.deviceType))
        })
    }, [])

    return {
        newDevicePrompts,
        deviceRemovedPrompts,
        removeNewDevicePromptItem,
        removeDeviceRemovedPromptItem,
    }
}

export type NewDevicePromptData = {
    deviceLabel: string
    deviceId: string
    deviceType: 'audioInput' | 'audioOutput' | 'video'
}

export type DeviceRemovedPromptData = {
    deviceLabel: string
    deviceId: string
    deviceType: 'audioInput' | 'audioOutput' | 'video'
}
